export const SNAP_AXES = ['x', 'y'] as const;
export type SnapAxis = (typeof SNAP_AXES)[number];

export type VertexFamily = 'entity' | 'grid';

export interface AxisRange {
  readonly lo: number;
  readonly hi: number;
}

/**
 * Describes the perpendicular extent of an `AxisSnapTarget` (on the cross axis):
 * - `point` — a concrete coordinate on the perpendicular axis (the target was a point).
 * - `range` — bounds the source point's perpendicular coordinate (the target was a segment or 3D object).
 * - `unbounded` — accepts any perpendicular value (the target was a line).
 */
export type AxisPerpendicular =
  | { readonly kind: 'point'; readonly value: number }
  | { readonly kind: 'range'; readonly range: AxisRange }
  | { readonly kind: 'unbounded' };

declare const AXIS_SNAP_TARGET_KEY_BRAND: unique symbol;
export type AxisSnapTargetKey = string & { readonly [AXIS_SNAP_TARGET_KEY_BRAND]: never };

/**
 * A scalar coordinate target consumed by interactive snapping. Point snaps become one target per axis; axis-aligned
 *   nearest-line snaps become a single target for the constrained axis, optionally bounded by the source segment.
 */
export interface AxisSnapTarget {
  readonly axis: SnapAxis;
  readonly value: number;
  readonly perpendicular: AxisPerpendicular;
  readonly key: AxisSnapTargetKey;
}

export interface MoveSoftLockState {
  readonly x: MoveAxisSoftLockState;
  readonly y: MoveAxisSoftLockState;
}

export type MoveAxisSoftLockState =
  | {
      readonly family: 'entity';
      readonly targetKey: AxisSnapTargetKey;
    }
  | {
      readonly family: 'grid';
      readonly target: GridSnapTarget;
    }
  | null;

export interface GridSnapTarget {
  readonly snappedValue: number;
}

export type LengthSoftLockState = AxisSnapTargetKey | null;

export function moveSoftLockStateEquals(a: MoveSoftLockState, b: MoveSoftLockState): boolean {
  return moveAxisSoftLockStateEquals(a.x, b.x) && moveAxisSoftLockStateEquals(a.y, b.y);
}

export function moveAxisSoftLockStateEquals(a: MoveAxisSoftLockState, b: MoveAxisSoftLockState): boolean {
  if (a === null || b === null) {
    return a === b;
  }

  if (a.family === 'entity') {
    return b.family === 'entity' && a.targetKey === b.targetKey;
  }

  return b.family === 'grid' && gridSnapTargetEquals(a.target, b.target);
}

export function gridSnapTargetEquals(a: GridSnapTarget | null, b: GridSnapTarget | null): boolean {
  if (a === null || b === null) return a === b;

  return a.snappedValue === b.snappedValue;
}
